import React, { useState, useEffect, useMemo } from 'react';
import { format, addDays, parseISO, isToday } from 'date-fns';
import { useStore } from '../../store/StoreContext';
import { HelpTip, Button, IconButton } from '../ui';
import JobModal from '../modals/JobModal';
import { formatTime12 } from '../../utils/formatters';
import { jobResourceStatus } from '../../utils/dispatchHelpers';
import { kindBadge, kindIsHoldOnly } from '../../lib/phaseKinds';
import { useToast } from '../../hooks/useToast';

// ===== DISPATCH — DAY VIEW v2 (vertical, 2026-08-03) =====
// Rebuilt to Jaylen's sketch after v1's horizontal Gantt didn't click:
// hours run DOWN like a calendar day; each crew lead is a column AND a
// colored thread — their line connects their jobs through the day, so a
// gap in the thread IS their free window. Jobs are nodes; when two nodes
// are legs of the SAME estimate handled by DIFFERENT crews, a dashed
// dark connector ties them together (the Supabase-schema-map feel he
// asked for). Same data as v1 — clicking a node still opens JobModal.

// Phase badges generated from segments (phaseKinds.js, 2026-08-26).

// "HH:MM" → fractional hours (8.5 for 08:30). Null on missing/garbage.
const timeToHours = (t) => {
    const m = String(t || '').match(/^(\d{1,2}):(\d{2})/);
    if (!m) return null;
    const h = parseInt(m[1], 10) + parseInt(m[2], 10) / 60;
    return Number.isFinite(h) ? h : null;
};

// "101 Test Ln, Hendersonville, NC 28739, USA" → "101 Test Ln, Hendersonville".
const shortAddr = (a) => a ? a.split(',').slice(0, 2).map(s => s.trim()).join(', ') : '';

// Layout constants (px). Nodes are absolutely positioned from these, so
// the connector SVG can be computed, not measured.
const AXIS_W = 88;      // hour-label gutter ("12:00 PM" needs the room)
const COL_W = 250;      // column pitch
const NODE_W = 218;     // card width
const TOP = 20;         // top padding (leads live ON the cards — round 2)
const PXH = 62;         // pixels per hour
const BOTTOM_PAD = 24;

export default function DispatchDayView({ viewSwitcher }) {
    const { jobs, clients, crew, trucks, estimates, saveJob, removeJob } = useStore();
    const { show: showToast } = useToast();
    const [dateIso, setDateIso] = useState(() => format(new Date(), 'yyyy-MM-dd'));
    const [editJob, setEditJob] = useState(null);
    // Re-render every minute so the "now" line crawls.
    const [, setTick] = useState(0);
    useEffect(() => {
        const h = setInterval(() => setTick(t => t + 1), 60 * 1000);
        return () => clearInterval(h);
    }, []);

    const day = parseISO(dateIso);
    const getClient = (id) => clients.find(c => c.id === id);
    const getCrew = (id) => crew.find(c => c.id === id);
    const getTruck = (id) => trucks.find(t => t.id === id);

    // Jobs touching this date. Mid-span days of multi-day jobs count but
    // carry no meaningful start time for THIS day — they go to the
    // strip below labeled "Day N of M".
    const { timed, untimed } = useMemo(() => {
        const timed = [], untimed = [];
        for (const j of (jobs || [])) {
            if (j._isSpanDay || j.status === 'cancelled' || !j.move_date) continue;
            const dur = Math.max(1, j.duration_days || 1);
            const covers = j.move_date === dateIso
                || (dur > 1 && j.move_date < dateIso && dateIso <= format(addDays(parseISO(j.move_date), dur - 1), 'yyyy-MM-dd'));
            if (!covers) continue;
            const midSpan = j.move_date !== dateIso;
            const isWt = j.type === 'WALKTHROUGH';
            // Ghost hold (pick C): a parked loaded truck has no start time
            // and no lane — it's a strip item every day it spans.
            const isHold = kindIsHoldOnly(j.phase_kind);
            const startH = (midSpan || isHold) ? null : timeToHours(j.time);
            if (startH === null) {
                if (!isWt) untimed.push({ job: j, midSpan, isHold, dayN: (midSpan || isHold) ? Math.round((parseISO(dateIso) - parseISO(j.move_date)) / 86400000) + 1 : null });
            } else {
                timed.push({ job: j, startH, durH: isWt ? 1 : Math.max(1, Number(j.duration) || 3), isWt });
            }
        }
        return { timed, untimed };
    }, [jobs, dateIso]);

    // Hour range: 7a–7p floor, stretched to fit the day's jobs.
    const rangeStart = Math.min(7, ...timed.map(t => Math.floor(t.startH)));
    const rangeEnd = Math.max(19, ...timed.map(t => Math.ceil(t.startH + t.durH)));
    const yFor = (h) => TOP + (h - rangeStart) * PXH;

    // Columns: one per crew lead with anything today (board crew order),
    // plus an amber Unassigned rail at the end when needed. Jobs with
    // crew but no lead fall to the first assigned member's column.
    const { columns, nodes, connectors } = useMemo(() => {
        // Walkthroughs share ONE column (Jaylen 2026-08-07) — a lane per
        // estimator burned a full column per dashed card, and the card
        // already names its estimator. Leftmost = the morning glance rail.
        const laneFor = (j) => j.type === 'WALKTHROUGH' ? '_walkthroughs' : (j.leader_id || (j.crew_ids || [])[0] || '_unassigned');
        const laneKeys = new Set(timed.map(t => laneFor(t.job)));
        const columns = [];
        if (laneKeys.has('_walkthroughs')) columns.push({ key: '_walkthroughs', member: null });
        columns.push(...crew.filter(m => laneKeys.has(m.id)).map(m => ({ key: m.id, member: m })));
        if (laneKeys.has('_unassigned')) columns.push({ key: '_unassigned', member: null });
        const colIndex = new Map(columns.map((c, i) => [c.key, i]));

        // Nodes with computed pixel geometry.
        const byCol = new Map();
        const nodes = timed.map(t => {
            const col = colIndex.get(laneFor(t.job)) ?? 0;
            const x = AXIS_W + col * COL_W + 8;
            const y = yFor(t.startH);
            const h = Math.max(44, t.durH * PXH - 4);
            return { ...t, col, x, y, h };
        }).sort((a, b) => a.startH - b.startH);
        for (const n of nodes) {
            const sibs = byCol.get(n.col) || [];
            sibs.push(n); byCol.set(n.col, sibs);
        }

        // De-overlap within each column (Jaylen 2026-08-07 Day view
        // revision — five 4h-est deliveries on one lead stacked into an
        // unreadable pile). Duration estimates PAD; in reality a lead
        // runs his jobs back-to-back. So a card's DRAWN height clamps to
        // the gap before that lead's next job: the start time stays true
        // on the axis, the header keeps the full "Nh est" text, and the
        // column reads as the day's actual sequence. Cards at (nearly)
        // the same start time compact + stack downward instead — their
        // printed time is still correct even if the pixel position
        // drifts a little. Card content already degrades by height
        // (lead at ≥78px, chips at ≥96px …), so short cards stay clean.
        const NODE_GAP = 6;
        const MIN_NODE_H = 44;
        for (const sibs of byCol.values()) {
            const sorted = [...sibs].sort((a, b) => a.y - b.y || a.startH - b.startH);
            for (let i = 0; i < sorted.length - 1; i++) {
                const n = sorted[i], next = sorted[i + 1];
                if (next.y >= n.y + n.h + NODE_GAP) continue;      // no overlap
                if (next.y - n.y >= MIN_NODE_H + NODE_GAP) {
                    n.h = next.y - n.y - NODE_GAP;                 // clamp est to the real gap
                } else {
                    n.h = Math.min(n.h, 64);                       // near-same start: compact + push
                    next.y = n.y + n.h + NODE_GAP;
                }
            }
        }

        // Lead threads: consecutive nodes in the same column (skip the
        // Unassigned rail — no "day path" for nobody).
        const threads = [];
        for (const [col, sibs] of byCol) {
            if (columns[col]?.key === '_unassigned' || columns[col]?.key === '_walkthroughs') continue;
            const sorted = [...sibs].sort((a, b) => a.startH - b.startH);
            for (let i = 0; i < sorted.length - 1; i++) {
                const a = sorted[i], b = sorted[i + 1];
                if (b.y > a.y + a.h) {
                    threads.push({ x: AXIS_W + col * COL_W + 8 + NODE_W / 2, y1: a.y + a.h, y2: b.y, color: columns[col].member?.display_color || '#64748b' });
                }
            }
        }

        // Cross-crew connectors: legs of the same estimate in DIFFERENT
        // columns. Drawn earlier-node → later-node with a midpoint tag.
        const connectors = [];
        const byEst = new Map();
        for (const n of nodes) {
            if (!n.job.estimate_id || n.isWt) continue;
            if (!byEst.has(n.job.estimate_id)) byEst.set(n.job.estimate_id, []);
            byEst.get(n.job.estimate_id).push(n);
        }
        for (const legs of byEst.values()) {
            const cols = new Set(legs.map(l => l.col));
            if (legs.length < 2 || cols.size < 2) continue;
            const sorted = [...legs].sort((a, b) => a.startH - b.startH);
            for (let i = 0; i < sorted.length - 1; i++) {
                const a = sorted[i], b = sorted[i + 1];
                if (a.col === b.col) continue;
                const ax = a.x + NODE_W / 2, ay = a.y + a.h;
                const bx = b.x + NODE_W / 2, by = b.y;
                connectors.push({
                    path: `M ${ax} ${ay} C ${ax} ${ay + 70}, ${bx} ${by - 70}, ${bx} ${by}`,
                    tagX: (ax + bx) / 2, tagY: (ay + by) / 2,
                    label: `${getClient(sorted[0].job.client_id)?.name?.split(' ')[0] || 'Linked'} · ${legs.length} legs, ${cols.size} crews`,
                });
            }
        }
        return { columns, nodes, connectors: { wires: connectors, threads } };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [timed, crew, clients, rangeStart]);

    const canvasW = AXIS_W + Math.max(1, columns.length) * COL_W + 24;
    // Max over the axis end AND every node bottom — the same-start
    // push-down can nudge a card slightly past its est envelope.
    const canvasH = Math.max(yFor(rangeEnd), ...nodes.map(n => n.y + n.h)) + BOTTOM_PAD;
    const hourMarks = [];
    for (let h = rangeStart; h <= rangeEnd; h++) hourMarks.push(h);

    const now = new Date();
    const nowH = now.getHours() + now.getMinutes() / 60;
    const showNow = isToday(day) && nowH >= rangeStart && nowH <= rangeEnd;

    const handleSave = async (form, deleteId) => {
        if (deleteId) { await removeJob(deleteId); showToast('Job deleted'); return; }
        await saveJob(form);
        showToast('Job updated');
    };

    const nodeTitle = (n) => {
        const client = getClient(n.job.client_id);
        const lead = getCrew(n.job.leader_id);
        const members = (n.job.crew_ids || []).filter(id => id !== n.job.leader_id).map(id => getCrew(id)?.name).filter(Boolean);
        const truckNames = (n.job.truck_ids || []).map(id => getTruck(id)?.name).filter(Boolean);
        return `${client?.name || 'Unknown'} · ${formatTime12(n.job.time)} · ${n.durH}h`
            + (lead ? ` · Lead: ${lead.name}` : '')
            + (members.length ? ` · Crew: ${members.join(', ')}` : '')
            + (truckNames.length ? ` · ${truckNames.join(', ')}` : '')
            + ` · ${n.job.status}`;
    };

    return (
        <div className="p-6">
            {/* Header */}
            <div className="flex flex-wrap items-center justify-between gap-y-2 mb-4">
                <div className="flex flex-wrap items-center gap-x-4 gap-y-2 min-w-0">
                    <div className="flex items-center gap-2.5">
                        <h1 className="font-bold font-lemonmilk text-slate-900 text-2xl">Dispatch Day</h1>
                        <HelpTip title="How the day view works">
                            <p>Hours run down like a calendar. Each <span className="font-semibold">crew lead has a column and a colored thread</span> — the line connects their jobs through the day, so a gap in the thread is their free window. Every card names its <span className="font-semibold">crew lead</span> with the crew listed under it (short cards move the roster to their hover tooltip). Walkthroughs are dashed cards in the estimator's column; jobs with no crew sit in the amber Unassigned rail.</p>
                            <p>A <span className="font-semibold">dark dashed connector</span> between two cards means they're legs of the SAME client's move handled by different crews (e.g. one crew loads in the morning, another does the labor leg that afternoon) — the tag on the line names the client.</p>
                            <p>Cards carry the operational detail: from → to addresses, a status chip (amber <span className="font-semibold">⚠ NOT DISPATCHED</span> when it's move day and the crew hasn't been texted), the "N/M trucks · N/M crew" requirements chip (amber when short), and 📝 🏋️ 🪜 flags for notes, heavy items, and stairs (hover for the detail). The summary line above the canvas totals the day: jobs, crew and trucks used vs available, walkthroughs.</p>
                            <p><span className="font-semibold">Tap any card</span> to open the job editor (same as Active Moves). The red line is now (today only); in-progress cards glow; completed cards dim. Jobs with no set time and mid-span days of multi-day runs list below the canvas.</p>
                        </HelpTip>
                    </div>
                    {viewSwitcher}
                </div>
                <div className="flex items-center gap-1">
                    <IconButton label="Previous day" size="sm" onClick={() => setDateIso(format(addDays(day, -1), 'yyyy-MM-dd'))}>←</IconButton>
                    <Button variant={isToday(day) ? 'primary' : 'secondary'} size="sm" onClick={() => setDateIso(format(new Date(), 'yyyy-MM-dd'))}>Today</Button>
                    <IconButton label="Next day" size="sm" onClick={() => setDateIso(format(addDays(day, 1), 'yyyy-MM-dd'))}>→</IconButton>
                    <span className="ml-2 font-semibold text-slate-800 text-sm whitespace-nowrap">{format(day, 'EEEE, MMM d, yyyy')}</span>
                </div>
            </div>

            {/* Day summary — the day's load before you read a card.
                Supply = active roster/fleet; demand = unique crew + trucks
                on the day's jobs (walkthroughs excluded, they're not load). */}
            {(nodes.length > 0 || untimed.length > 0) && (() => {
                const holds = untimed.filter(u => u.isHold).map(u => u.job);
                const dayMoves = [...nodes.filter(n => !n.isWt).map(n => n.job), ...untimed.filter(u => !u.isHold).map(u => u.job)];
                const wtCount = nodes.filter(n => n.isWt).length;
                const crewUsed = new Set(dayMoves.flatMap(j => j.crew_ids || [])).size;
                // Parked loaded trucks count as trucks in use (they're not free).
                const trucksUsed = new Set([...dayMoves, ...holds].flatMap(j => j.truck_ids || [])).size;
                const crewTotal = crew.filter(c => c.active !== false).length;
                const truckTotal = trucks.filter(t => t.active !== false).length;
                const undispatched = dayMoves.filter(j => j.status === 'assigned' || j.status === 'unassigned').length;
                return (
                    <div className="mb-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-[13px] text-slate-600 font-medium">
                        <span><b className="text-slate-900">{dayMoves.length}</b> job{dayMoves.length !== 1 ? 's' : ''}</span>
                        <span><b className="text-slate-900">{crewUsed}</b>/{crewTotal} crew</span>
                        <span><b className="text-slate-900">{trucksUsed}</b>/{truckTotal} trucks</span>
                        {wtCount > 0 && <span><b className="text-slate-900">{wtCount}</b> walkthrough{wtCount !== 1 ? 's' : ''}</span>}
                        {holds.length > 0 && <span><b className="text-slate-900">{holds.length}</b> truck{holds.length !== 1 ? 's' : ''} holding</span>}
                        {undispatched > 0 && dateIso <= format(new Date(), 'yyyy-MM-dd') && (
                            <span className="text-amber-700 font-bold">⚠ {undispatched} not dispatched</span>
                        )}
                    </div>
                );
            })()}

            {nodes.length === 0 && untimed.length === 0 ? (
                <div className="bg-white border border-slate-200 rounded-xl p-12 text-center text-slate-400">
                    Nothing scheduled for {format(day, 'EEE, MMM d')}.
                </div>
            ) : (
                <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto">
                    <div className="relative" style={{ width: canvasW, height: canvasH, minWidth: '100%' }}>
                        {/* hour lines + labels. The label is vertically
                            CENTERED on its gridline (translate-y-1/2) so
                            "1:00 PM" reads as the line itself, and card
                            tops land exactly on the line they start at. */}
                        {hourMarks.map(h => (
                            <div key={h} className="absolute left-0 right-0 border-t border-slate-100" style={{ top: yFor(h) }}>
                                <span className="absolute left-2.5 top-0 -translate-y-1/2 text-[11px] font-bold text-slate-400 bg-white px-1">
                                    {`${h === 12 ? 12 : h > 12 ? h - 12 : h}:00 ${h >= 12 ? 'PM' : 'AM'}`}
                                </span>
                            </div>
                        ))}

                        {/* No column headers — the lead is named ON each
                            card (Jaylen round 2); the thread + color still
                            group a lead's day visually. */}

                        {/* threads + connectors (under nodes) */}
                        <svg className="absolute inset-0 pointer-events-none" width={canvasW} height={canvasH} style={{ zIndex: 1 }}>
                            {connectors.threads.map((t, i) => (
                                <line key={'t' + i} x1={t.x} y1={t.y1} x2={t.x} y2={t.y2} stroke={t.color} strokeWidth="3" opacity="0.45" />
                            ))}
                            {connectors.wires.map((w, i) => (
                                <path key={'w' + i} d={w.path} fill="none" stroke="#0f172a" strokeWidth="2.5" strokeDasharray="7 5" opacity="0.55" />
                            ))}
                        </svg>
                        {connectors.wires.map((w, i) => (
                            <div key={'tag' + i} className="absolute z-[3] text-[10px] font-extrabold bg-slate-900 text-white px-2 py-0.5 rounded-full shadow -translate-x-1/2 -translate-y-1/2 pointer-events-none" style={{ left: w.tagX, top: w.tagY }}>
                                {w.label}
                            </div>
                        ))}

                        {/* now line */}
                        {showNow && (
                            <div className="absolute border-t-2 border-red-500 z-[5] pointer-events-none" style={{ left: AXIS_W, right: 0, top: yFor(nowH) }}>
                                <span className="absolute -left-1.5 -top-[5px] w-2 h-2 rounded-full bg-red-500"></span>
                            </div>
                        )}

                        {/* nodes — card anatomy (Jaylen round 3):
                            TOP half:    client name (large) → type · time · est hours (+ phase)
                            BOTTOM half: crew lead (large) → members → trucks →
                                         "N/M trucks · N/M crew" requirements line.
                            Lines appear progressively as the card gets taller;
                            short cards keep the full roster in the tooltip. */}
                        {nodes.map((n, i) => {
                            const client = getClient(n.job.client_id);
                            const col = columns[n.col];
                            const badge = n.job.phase_kind ? kindBadge(n.job.phase_kind) : null;
                            const isDone = n.job.status === 'completed';
                            const isLive = n.job.status === 'in_progress';
                            const isUnassigned = col?.key === '_unassigned';
                            // Shared walkthroughs column: the estimator comes
                            // from the JOB, not the column.
                            const wtEst = n.isWt ? getCrew(n.job.leader_id || (n.job.crew_ids || [])[0]) : null;
                            const truckNames = (n.job.truck_ids || []).map(id => getTruck(id)?.name).filter(Boolean);
                            const typeLabel = (n.job.type || '').replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
                            // Requirements vs assignment. With a linked estimate we use
                            // the shared helper (same math as the board pill's warning);
                            // without one, the job's own planned crew_size is the bar.
                            const est = n.job.estimate_id ? estimates.find(e => e.id === n.job.estimate_id) : null;
                            const rs = est ? jobResourceStatus(n.job, est, trucks) : null;
                            const reqCrew = rs ? rs.requiredCrew : (Number(n.job.crew_size) || 0);
                            const reqTrucks = rs ? rs.requiredTrucks : 0;
                            const gotCrew = (n.job.crew_ids || []).length;
                            const gotTrucks = truckNames.length;
                            const short = (reqCrew > 0 && gotCrew < reqCrew) || (reqTrucks > 0 && gotTrucks < reqTrucks);
                            const reqParts = [];
                            if (reqTrucks > 0 || gotTrucks > 0) reqParts.push(`${gotTrucks}/${Math.max(reqTrucks, gotTrucks)} trucks`);
                            if (reqCrew > 0 || gotCrew > 0) reqParts.push(`${gotCrew}/${Math.max(reqCrew, gotCrew)} crew`);
                            const members = (n.job.crew_ids || []).filter(id => id !== (col?.member?.id)).map(id => getCrew(id)?.name).filter(Boolean);
                            return (
                                <button
                                    key={n.job.id + '-' + i}
                                    onClick={() => setEditJob(n.job)}
                                    title={nodeTitle(n)}
                                    className={`absolute rounded-xl text-left px-3 py-2 overflow-hidden shadow-sm z-[2] flex flex-col ${n.isWt
                                        ? 'bg-white text-slate-700 border-2 border-dashed'
                                        : 'text-white'} ${isLive ? 'dispatch-pill-inprogress' : ''} ${isDone ? 'opacity-50' : ''}`}
                                    style={{
                                        left: n.x, top: n.y, height: n.h, width: NODE_W,
                                        ...(n.isWt
                                            ? { borderColor: wtEst?.display_color || '#f87171' }
                                            : { backgroundColor: isUnassigned ? '#f59e0b' : (col?.member?.display_color || '#64748b') }),
                                    }}
                                >
                                    {/* ── top half: the job ── */}
                                    <div className="min-w-0">
                                        <span className={`font-extrabold block truncate leading-tight ${n.h >= 96 ? 'text-[15px]' : 'text-[13px]'}`}>{client?.name || 'Unknown'}</span>
                                        <span className={`text-[11px] flex items-center gap-1.5 leading-tight flex-wrap ${n.isWt ? 'text-slate-500' : 'opacity-90'}`}>
                                            {n.isWt ? 'Walkthrough · ' : `${typeLabel} · `}{formatTime12(n.job.time)}{!n.isWt && ` · ${n.durH}h est`}
                                            {badge && <span className="px-1 rounded text-[9.5px] font-extrabold" style={{ background: badge.bg, color: badge.fg }}>{badge.label}</span>}
                                            {/* gotcha flags — the read-before-you-send markers */}
                                            {!!(n.job.dispatcher_notes || n.job.special_notes) && <span title={`Notes: ${n.job.dispatcher_notes || n.job.special_notes}`}>📝</span>}
                                            {!!n.job.heavy_items && <span title={`Heavy items: ${n.job.heavy_items}`}>🏋️</span>}
                                            {!!n.job.stairs && <span title={`Stairs${n.job.levels ? ` (${n.job.levels} levels)` : ''}`}>🪜</span>}
                                        </span>
                                        {n.isWt && (
                                            <span className="text-[10.5px] block truncate font-semibold" style={{ color: wtEst?.display_color || '#94a3b8' }}>
                                                {wtEst ? `Estimator: ${wtEst.name}` : 'No estimator assigned'}
                                            </span>
                                        )}
                                        {!n.isWt && n.h >= 150 && (n.job.from_address || n.job.to_address) && (
                                            <span className="text-[10px] opacity-85 block leading-snug mt-1">
                                                {n.job.from_address && <span className="block truncate">From: {shortAddr(n.job.from_address)}</span>}
                                                {n.job.to_address && <span className="block truncate">To: {shortAddr(n.job.to_address)}</span>}
                                            </span>
                                        )}
                                    </div>
                                    {/* ── bottom half: the crew ── */}
                                    {!n.isWt && (
                                        <div className="mt-auto min-w-0 pt-1">
                                            {!isUnassigned && col?.member && n.h >= 78 && (
                                                <span className="text-[13px] font-extrabold block truncate leading-tight">Lead: {col.member.name}</span>
                                            )}
                                            {members.length > 0 && n.h >= 118 && (
                                                <span className="text-[10px] opacity-85 block leading-snug" style={{ whiteSpace: 'normal' }}>Crew: {members.join(', ')}</span>
                                            )}
                                            {truckNames.length > 0 && n.h >= 140 && (
                                                <span className="text-[10px] opacity-85 block truncate">{truckNames.join(' + ')}</span>
                                            )}
                                            {n.h >= 96 && (
                                                <span className="flex items-center gap-1 flex-wrap mt-0.5">
                                                    {reqParts.length > 0 && (
                                                        <span className={`text-[10px] font-extrabold inline-block px-1.5 py-0.5 rounded ${short ? 'bg-amber-300/95 text-amber-950' : 'bg-black/20'}`}>
                                                            {short && '⚠ '}{reqParts.join(' · ')}
                                                        </span>
                                                    )}
                                                    {/* Status chip — "assigned" on a day that's here (or past)
                                                        means the crew was never texted: amber alarm. Future
                                                        days show it neutrally. */}
                                                    {!isUnassigned && (n.job.status === 'assigned' ? (
                                                        <span className={`text-[10px] font-extrabold inline-block px-1.5 py-0.5 rounded ${dateIso <= format(new Date(), 'yyyy-MM-dd') ? 'bg-amber-300/95 text-amber-950' : 'bg-black/20'}`}>
                                                            {dateIso <= format(new Date(), 'yyyy-MM-dd') ? '⚠ NOT DISPATCHED' : 'ASSIGNED'}
                                                        </span>
                                                    ) : n.job.status === 'dispatched' ? (
                                                        <span className="text-[10px] font-extrabold inline-block px-1.5 py-0.5 rounded bg-black/20">DISPATCHED</span>
                                                    ) : n.job.status === 'in_progress' ? (
                                                        <span className="text-[10px] font-extrabold inline-block px-1.5 py-0.5 rounded bg-white/25">IN PROGRESS</span>
                                                    ) : n.job.status === 'completed' ? (
                                                        <span className="text-[10px] font-extrabold inline-block px-1.5 py-0.5 rounded bg-black/20">✓ DONE</span>
                                                    ) : null)}
                                                </span>
                                            )}
                                        </div>
                                    )}
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* No-time / mid-span strip */}
            {untimed.length > 0 && (
                <div className="mt-3 bg-white border border-slate-200 rounded-xl p-3">
                    <div className="text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-2">No set time / spanning this day</div>
                    <div className="flex flex-wrap gap-2">
                        {untimed.map(({ job, midSpan, isHold, dayN }) => {
                            const client = getClient(job.client_id);
                            const lead = getCrew(job.leader_id);
                            const badge = job.phase_kind ? kindBadge(job.phase_kind) : null;
                            const days = Math.max(1, job.duration_days || 1);
                            const truckNames = isHold ? (job.truck_ids || []).map(id => getTruck(id)?.name).filter(Boolean).join(' + ') : '';
                            return (
                                <button key={job.id} onClick={() => setEditJob(job)}
                                    className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-slate-200 bg-slate-50 hover:bg-slate-100 text-left">
                                    {lead && !isHold && <span className="w-2 h-5 rounded-full" style={{ backgroundColor: lead.display_color || '#64748b' }}></span>}
                                    <span className="text-xs font-semibold text-slate-800">{client?.name || 'Unknown'}</span>
                                    {badge && <span className="px-1 rounded text-[10px] font-bold" style={{ background: badge.bg, color: badge.fg }}>{badge.label}</span>}
                                    <span className="text-[10px] text-slate-500">
                                        {isHold ? `Truck parked${truckNames ? ` · ${truckNames}` : ''}${days > 1 ? ` · day ${dayN} of ${days}` : ''}` : midSpan ? `Day ${dayN} of ${job.duration_days}` : 'No time set'}
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}

            {editJob && (
                <JobModal
                    job={editJob}
                    onClose={() => setEditJob(null)}
                    onSave={async (form, deleteId) => { await handleSave(form, deleteId); setEditJob(null); }}
                    clients={clients} crew={crew} trucks={trucks} jobs={jobs}
                />
            )}
        </div>
    );
}
